import { calculateCostUsd } from "./costCalculator";
import { SEED_EXECUTION_STORIES } from "./seed";

function safeJsonParse(value) {
    try {
        return JSON.parse(value);
    } catch {
        return null;
    }
}

function normalizeStatus(status) {
    if (!status) return "success";
    const normalized = String(status).toLowerCase();
    if (normalized === "failed") return "error";
    return normalized;
}

function normalizeExecutionStory(payload) {
    const metadata = payload.metadata || {};
    const inputTokens = Number(payload.input_tokens ?? metadata.input_tokens ?? 0);
    const outputTokens = Number(payload.output_tokens ?? metadata.output_tokens ?? 0);
    const totalTokens = Number(payload.total_tokens ?? metadata.total_tokens ?? inputTokens + outputTokens);
    const model = (Array.isArray(payload.models_used) && payload.models_used[0]) || payload.model || "gpt-5.2";
    const bedrockReasoningEstimatedTokens = Number(
        payload.bedrock_reasoning_estimated_tokens ?? metadata.bedrock_reasoning_estimated_tokens ?? 0
    );
    const bedrockReasoningEstimatedCostUsd = Number(
        payload.bedrock_reasoning_estimated_cost_usd ?? metadata.bedrock_reasoning_estimated_cost_usd ?? 0
    );
    const resolvedBedrockReasoningEstimatedCostUsd =
        bedrockReasoningEstimatedCostUsd > 0 || bedrockReasoningEstimatedTokens <= 0
            ? bedrockReasoningEstimatedCostUsd
            : calculateCostUsd({
                inputTokens: 0,
                outputTokens: bedrockReasoningEstimatedTokens,
                model,
            });
    const visibleCostUsd = calculateCostUsd({
        inputTokens,
        outputTokens: Math.max(0, outputTokens - bedrockReasoningEstimatedTokens),
        model,
    });

    return {
        ...payload,
        log_type: "execution_story",
        timestamp: payload.timestamp || payload.ended_at || payload.started_at || payload.ts,
        status: normalizeStatus(payload.status),
        feature: payload.feature || payload.brain || "other",
        user_id: payload.user_id || metadata.user_id || "unknown_user",
        client_id: payload.client_id || metadata.client_id || "unknown_client",
        input_tokens: inputTokens,
        output_tokens: outputTokens,
        total_tokens: totalTokens,
        model,
        bedrock_reasoning_enabled: Boolean(payload.bedrock_reasoning_enabled ?? metadata.bedrock_reasoning_enabled ?? false),
        bedrock_reasoning_public_stream_enabled: Boolean(
            payload.bedrock_reasoning_public_stream_enabled ?? metadata.bedrock_reasoning_public_stream_enabled ?? false
        ),
        bedrock_reasoning_budget_tokens: Number(payload.bedrock_reasoning_budget_tokens ?? metadata.bedrock_reasoning_budget_tokens ?? 0),
        bedrock_reasoning_delta_count: Number(payload.bedrock_reasoning_delta_count ?? metadata.bedrock_reasoning_delta_count ?? 0),
        bedrock_reasoning_forwarded_count: Number(payload.bedrock_reasoning_forwarded_count ?? metadata.bedrock_reasoning_forwarded_count ?? 0),
        bedrock_reasoning_chars_received: Number(payload.bedrock_reasoning_chars_received ?? metadata.bedrock_reasoning_chars_received ?? 0),
        bedrock_reasoning_chars_forwarded: Number(payload.bedrock_reasoning_chars_forwarded ?? metadata.bedrock_reasoning_chars_forwarded ?? 0),
        bedrock_reasoning_estimated_tokens: bedrockReasoningEstimatedTokens,
        bedrock_reasoning_estimated_cost_usd: resolvedBedrockReasoningEstimatedCostUsd,
        bedrock_reasoning_token_source: payload.bedrock_reasoning_token_source ?? metadata.bedrock_reasoning_token_source ?? "unavailable",
        cost_usd: visibleCostUsd + resolvedBedrockReasoningEstimatedCostUsd,
        error_flag: normalizeStatus(payload.status) === "error" || (Array.isArray(payload.errors) && payload.errors.length > 0),
    };
}

function normalizeEventLog(payload) {
    const metadata = payload.metadata || {};
    const rawStatus = payload?.data?.status || payload.status;
    const normalizedStatus = normalizeStatus(rawStatus);
    return {
        ...payload,
        log_type: "event_log",
        ts: payload.ts || payload.timestamp,
        status: normalizedStatus,
        type: payload.type || payload.event || payload.log_type || "event",
        step: payload.step || payload.event || payload.log_type || "event",
        user_id: metadata.user_id || payload.user_id || "unknown_user",
        client_id: metadata.client_id || payload.client_id || "unknown_client",
    };
}

function isEventLikeLogType(logType) {
    const value = String(logType || "").toLowerCase();
    if (!value) return false;
    if (value === "event_log") return true;
    if (value.startsWith("thinking_")) return true;
    return value === "retry" || value === "critical_error";
}

function normalizeReviewFinalizeLog(payload) {
    return {
        ...payload,
        log_type: "review_finalize",
        timestamp: payload.timestamp || payload.ts,
        status: payload.event === "stream_error_forced_done" ? "error" : "info",
        request_id: payload.request_id || "unknown",
        document_version_id: payload.document_version_id || "unknown",
        criterion_ids: Array.isArray(payload.criterion_ids) ? payload.criterion_ids : [],
    };
}

function parseInfoLine(line) {
    const infoRegex = /^INFO:\s*(?<method>GET|POST|PUT|PATCH|DELETE|OPTIONS)\s+(?<path>\S+)\s+-\s+(?<status>\d{3})/i;
    const match = line.match(infoRegex);
    if (!match?.groups) {
        return { raw: line };
    }

    return {
        raw: line,
        method: match.groups.method,
        path: match.groups.path,
        status: Number(match.groups.status),
    };
}

function parseLogLines(rawText) {
    const lines = String(rawText || "").split(/\r?\n/);
    const parsedJsonBlocks = [];
    const infoLogs = [];
    const parseErrors = [];
    let currentBuffer = "";

    const flushBuffer = (lineNumber) => {
        if (!currentBuffer.trim()) return;
        const parsed = safeJsonParse(currentBuffer);
        if (parsed) {
            parsedJsonBlocks.push(parsed);
        } else {
            parseErrors.push({ lineNumber, raw: currentBuffer.slice(0, 220) });
        }
        currentBuffer = "";
    };

    lines.forEach((line, index) => {
        const trimmed = line.trim();
        if (!trimmed) {
            if (currentBuffer) {
                const parsed = safeJsonParse(currentBuffer);
                if (parsed) {
                    parsedJsonBlocks.push(parsed);
                    currentBuffer = "";
                }
            }
            return;
        }

        if (trimmed.startsWith("INFO:")) {
            if (currentBuffer) {
                flushBuffer(index + 1);
            }
            infoLogs.push(parseInfoLine(trimmed));
            return;
        }

        if (currentBuffer) {
            currentBuffer += `\n${trimmed}`;
            const parsed = safeJsonParse(currentBuffer);
            if (parsed) {
                parsedJsonBlocks.push(parsed);
                currentBuffer = "";
            }
            return;
        }

        if (trimmed.startsWith("{")) {
            const parsed = safeJsonParse(trimmed);
            if (parsed) {
                parsedJsonBlocks.push(parsed);
            } else {
                currentBuffer = trimmed;
            }
        }
    });

    if (currentBuffer) {
        flushBuffer(lines.length);
    }

    return { parsedJsonBlocks, infoLogs, parseErrors };
}

export function parseObservabilityLogs(rawText) {
    const { parsedJsonBlocks, infoLogs, parseErrors } = parseLogLines(rawText);

    const executionStories = [];
    const eventLogs = [];
    const reviewFinalizeLogs = [];

    parsedJsonBlocks.forEach((obj) => {
        if (obj?.log_type === "execution_story") {
            executionStories.push(normalizeExecutionStory(obj));
            return;
        }
        if (isEventLikeLogType(obj?.log_type)) {
            eventLogs.push(normalizeEventLog(obj));
            return;
        }
        if (obj?.log_type === "review_finalize") {
            reviewFinalizeLogs.push(normalizeReviewFinalizeLog(obj));
        }
    });

    const indexed = {
        byRequestId: new Map(),
        byUserId: new Map(),
        byClientId: new Map(),
        byFeature: new Map(),
        byService: new Map(),
    };

    executionStories.forEach((story) => {
        const push = (map, key) => {
            const list = map.get(key) || [];
            list.push(story);
            map.set(key, list);
        };

        push(indexed.byRequestId, story.request_id || "unknown", story);
        push(indexed.byUserId, story.user_id || "unknown", story);
        push(indexed.byClientId, story.client_id || "unknown", story);
        push(indexed.byFeature, story.feature || "other", story);
        push(indexed.byService, story.service || "unknown", story);
    });

    return {
        executionStories,
        eventLogs,
        reviewFinalizeLogs,
        infoLogs,
        parseErrors,
        indexed,
    };
}

export function buildSeedRawText() {
    const infoLines = [
        "INFO: GET /api/analyses/summary - 200",
        "INFO: POST /v1/contract-profiles/stream - 200",
    ];
    const jsonLines = SEED_EXECUTION_STORIES.slice(0, 10).map((item) => JSON.stringify(item));
    return [...infoLines, ...jsonLines].join("\n");
}
